import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ResearchWorkflowDemo = () => {
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = [
    {
      title: "Search for a Company",
      description: "Enter a ticker symbol or company name to begin your research",
      image: "/images/workflow-search.png",
      features: [
        "Real-time ticker search with autocomplete",
        "Company logo and basic information display",
        "Quick access to previously researched companies"
      ]
    },
    {
      title: "Situation Analysis",
      description: "Review AI-generated company overview and financial metrics",
      image: "/images/workflow-situation.png",
      features: [
        "Comprehensive company profile with key metrics",
        "Financial performance indicators and trends",
        "Market position and competitive landscape",
        "Recent news sentiment analysis"
      ]
    },
    {
      title: "Complication Identification",
      description: "Select from AI-generated complications or create your own",
      image: "/images/workflow-complication.png",
      features: [
        "AI-identified potential business challenges",
        "Risk categorization and prioritization",
        "Evidence-based complication analysis",
        "Custom complication creation option"
      ]
    },
    {
      title: "Question Formulation",
      description: "Generate key research questions based on selected complications",
      image: "/images/workflow-question.png",
      features: [
        "AI-generated research questions",
        "Question refinement and customization",
        "Focus on investment-relevant inquiries",
        "One-click question generation"
      ]
    },
    {
      title: "Hypothesis Development",
      description: "Create and evaluate investment hypotheses",
      image: "/images/workflow-hypothesis.png",
      features: [
        "Multiple hypothesis generation",
        "Supporting evidence and counterarguments",
        "Probability assessment",
        "Investment implication analysis"
      ]
    },
    {
      title: "Report Generation",
      description: "Generate a comprehensive equity research report with one click",
      image: "/images/workflow-report.png",
      features: [
        "Professional PDF report creation",
        "Complete SCQA analysis documentation",
        "Financial data visualization",
        "Investment recommendation with supporting rationale"
      ]
    }
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <motion.h2 
            className="text-3xl font-bold text-midnight-navy mb-4"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            See Research Genie in Action
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            transition={{ delay: 0.1 }}
          >
            Follow the research workflow from company selection to final report generation
          </motion.p>
        </div>
        
        {/* Step Navigation */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {steps.map((step, index) => (
            <button
              key={index}
              onClick={() => setActiveStep(index)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                activeStep === index 
                  ? "bg-electric-blue text-white" 
                  : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
            >
              {index + 1}. {step.title}
            </button>
          ))}
        </div>
        
        {/* Active Step Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
          {/* Step Description */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            key={`text-${activeStep}`}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="inline-block bg-electric-blue bg-opacity-10 text-electric-blue px-3 py-1 rounded-full text-sm font-medium mb-4">
                Step {activeStep + 1}
              </div>
              <h3 className="text-2xl font-bold text-midnight-navy mb-4">{steps[activeStep].title}</h3>
              <p className="text-gray-600 mb-6">{steps[activeStep].description}</p>
              
              <h4 className="font-semibold text-midnight-navy mb-3">Key Features:</h4>
              <ul className="space-y-3 mb-8">
                {steps[activeStep].features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {activeStep < steps.length - 1 ? (
                <button 
                  onClick={() => setActiveStep(activeStep + 1)}
                  className="bg-electric-blue text-white px-6 py-2 rounded-lg font-medium hover:bg-opacity-90 transition-colors inline-flex items-center"
                >
                  Next Step
                  <svg className="h-5 w-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ) : (
                <a 
                  href="https://equity-research-analyst-611846019618.us-central1.run.app/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-pale-gold text-midnight-navy px-6 py-2 rounded-lg font-medium hover:bg-opacity-90 transition-colors inline-flex items-center"
                >
                  Try It Yourself
                  <svg className="h-5 w-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              )}
            </div>
          </motion.div>
          
          {/* Step Screenshot */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            key={`image-${activeStep}`}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="order-first md:order-last"
          >
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
              {/* Placeholder for actual screenshots */}
              <div className="aspect-w-16 aspect-h-12 bg-gray-100 flex items-center justify-center">
                {activeStep === 0 && (
                  <div className="p-8 w-full">
                    <div className="bg-white rounded-lg shadow-md p-4 max-w-md mx-auto">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-midnight-navy">Dashboard</h3>
                        <button className="bg-electric-blue text-white px-3 py-1 rounded-md text-sm">+ Add New</button>
                      </div>
                      <div className="relative mb-4">
                        <input 
                          type="text" 
                          placeholder="Search tickers..." 
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 pl-10"
                        />
                        <svg className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Symbol</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">JDW.L</td>
                              <td className="px-4 py-3 text-sm text-gray-500">J D Wetherspoon plc</td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Completed</span>
                              </td>
                            </tr>
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">RNWH.L</td>
                              <td className="px-4 py-3 text-sm text-gray-500">Renew Holdings plc</td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Completed</span>
                              </td>
                            </tr>
                            <tr className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">DLG.L</td>
                              <td className="px-4 py-3 text-sm text-gray-500">Direct Line Insurance Group plc</td>
                              <td className="px-4 py-3 text-sm">
                                <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">In Progress</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeStep === 1 && (
                  <div className="p-8 w-full">
                    <div className="bg-white rounded-lg shadow-md p-4 max-w-md mx-auto">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-electric-blue text-white flex items-center justify-center mr-3">
                          <span className="font-bold">S</span>
                        </div>
                        <h3 className="text-lg font-semibold text-midnight-navy">Situation</h3>
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex justify-between mb-2">
                          <div>
                            <span className="text-sm text-gray-500">Market Cap</span>
                            <p className="font-semibold">£690.26M</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Current Price</span>
                            <p className="font-semibold">£631.70</p>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <div>
                            <span className="text-sm text-gray-500">Volume</span>
                            <p className="font-semibold">139,092</p>
                          </div>
                          <div>
                            <span className="text-sm text-gray-500">Exchange</span>
                            <p className="font-semibold">London Stock Exchange</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gray-50 p-3 rounded-lg mb-4">
                        <p className="text-sm text-gray-700">
                          J D Wetherspoon plc (JDW.L), a key player in the consumer cyclical sector, operates within the restaurant industry. With a market capitalization of £689.49 million, the company is a significant entity in the UK and Republic of Ireland's hospitality landscape.
                        </p>
                      </div>
                      
                      <div className="flex justify-between">
                        <button className="text-gray-500 px-3 py-1 rounded-md text-sm border border-gray-300">
                          Back
                        </button>
                        <button className="bg-electric-blue text-white px-3 py-1 rounded-md text-sm">
                          Next: Complication
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeStep === 2 && (
                  <div className="p-8 w-full">
                    <div className="bg-white rounded-lg shadow-md p-4 max-w-md mx-auto">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-electric-blue text-white flex items-center justify-center mr-3">
                          <span className="font-bold">C</span>
                        </div>
                        <h3 className="text-lg font-semibold text-midnight-navy">Complications</h3>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-4">Select one complication for JDW.L</p>
                      
                      <div className="space-y-3 mb-4">
                        <div className="border border-gray-200 rounded-lg p-3 hover:border-electric-blue cursor-pointer">
                          <div className="flex items-center mb-2">
                            <input type="radio" name="complication" className="mr-2" />
                            <h4 className="font-medium">Cost Increases and Potential Price Hikes</h4>
                          </div>
                          <p className="text-sm text-gray-600">
                            JD Wetherspoon is facing significant cost increases, particularly in staffing, which is expected to impact the company's profitability.
                          </p>
                        </div>
                        
                        <div className="border border-electric-blue rounded-lg p-3 bg-blue-50 cursor-pointer">
                          <div className="flex items-center mb-2">
                            <input type="radio" name="complication" className="mr-2" checked />
                            <h4 className="font-medium">Supply Chain Disruptions</h4>
                          </div>
                          <p className="text-sm text-gray-600">
                            The global supply chain crisis is a significant risk for JD Wetherspoon. Disruptions could impact the company's ability to source key inputs.
                          </p>
                        </div>
                        
                        <div className="border border-gray-200 rounded-lg p-3 hover:border-electric-blue cursor-pointer">
                          <div className="flex items-center mb-2">
                            <input type="radio" name="complication" className="mr-2" />
                            <h4 className="font-medium">Regulatory and Legal Risks</h4>
                          </div>
                          <p className="text-sm text-gray-600">
                            JD Wetherspoon is involved in a legal dispute over the use of its name, which could lead to reputational damage and associated costs.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex justify-between">
                        <button className="text-gray-500 px-3 py-1 rounded-md text-sm border border-gray-300">
                          Back to Situation
                        </button>
                        <button className="bg-electric-blue text-white px-3 py-1 rounded-md text-sm">
                          Next: Question
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeStep === 3 && (
                  <div className="p-8 w-full">
                    <div className="bg-white rounded-lg shadow-md p-4 max-w-md mx-auto">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-electric-blue text-white flex items-center justify-center mr-3">
                          <span className="font-bold">Q</span>
                        </div>
                        <h3 className="text-lg font-semibold text-midnight-navy">Key Question</h3>
                      </div>
                      
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <p className="text-sm text-gray-700">
                          How might global supply chain disruptions increase operational costs for JD Wetherspoon, potentially impacting customer satisfaction, revenue generation, and ultimately the company's market valuation in the medium to long term?
                        </p>
                      </div>
                      
                      <div className="flex justify-between mb-4">
                        <button className="bg-purple-100 text-purple-700 px-3 py-1 rounded-md text-sm flex items-center">
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Add New Hypothesis
                        </button>
                        <button className="bg-purple-600 text-white px-3 py-1 rounded-md text-sm flex items-center">
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                          Generate Question
                        </button>
                      </div>
                      
                      <div className="flex justify-between">
                        <button className="text-gray-500 px-3 py-1 rounded-md text-sm border border-gray-300">
                          Back to Complication
                        </button>
                        <button className="bg-electric-blue text-white px-3 py-1 rounded-md text-sm">
                          Next: Hypothesis
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeStep === 4 && (
                  <div className="p-8 w-full">
                    <div className="bg-white rounded-lg shadow-md p-4 max-w-md mx-auto">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-electric-blue text-white flex items-center justify-center mr-3">
                          <span className="font-bold">H</span>
                        </div>
                        <h3 className="text-lg font-semibold text-midnight-navy">Hypotheses</h3>
                      </div>
                      
                      <div className="space-y-4 mb-4">
                        <div className="border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium mb-2">"Increased Operational Costs Lead to Price Hikes"</h4>
                          <p className="text-sm text-gray-600">
                            JD Wetherspoon, due to supply chain disruptions, may face increased operational costs. To maintain profitability, the company might choose to pass these costs onto consumers by increasing prices.
                          </p>
                          <div className="flex mt-2">
                            <button className="text-blue-600 mr-2">
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button className="text-red-600">
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        
                        <div className="border border-gray-200 rounded-lg p-3">
                          <h4 className="font-medium mb-2">"Operational Disruptions Cause Service Quality Decline"</h4>
                          <p className="text-sm text-gray-600">
                            Supply chain disruptions could lead to shortages of key products, impacting JD Wetherspoon's ability to offer a full range of services. This could lead to a decline in service quality.
                          </p>
                          <div className="flex mt-2">
                            <button className="text-blue-600 mr-2">
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button className="text-red-600">
                              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between">
                        <button className="text-gray-500 px-3 py-1 rounded-md text-sm border border-gray-300">
                          Back to Question
                        </button>
                        <button className="bg-electric-blue text-white px-3 py-1 rounded-md text-sm">
                          Next: Process Hypothesis
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                
                {activeStep === 5 && (
                  <div className="p-8 w-full">
                    <div className="bg-white rounded-lg shadow-md p-4 max-w-md mx-auto">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 rounded-full bg-electric-blue text-white flex items-center justify-center mr-3">
                          <span className="font-bold">R</span>
                        </div>
                        <h3 className="text-lg font-semibold text-midnight-navy">Research Report</h3>
                      </div>
                      
                      <div className="border border-gray-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <svg className="h-6 w-6 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="font-medium">JDW.L Equity Research Report</span>
                          </div>
                          <span className="text-sm text-gray-500">April 26, 2025</span>
                        </div>
                        
                        <div className="bg-gray-50 p-3 rounded-lg mb-3">
                          <h4 className="font-medium mb-1">Executive Summary</h4>
                          <p className="text-sm text-gray-600">
                            This report analyzes the potential impact of global supply chain disruptions on JD Wetherspoon's operational costs and market valuation...
                          </p>
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">
                            SCQA Analysis
                          </div>
                          <div className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                            Financial Data
                          </div>
                          <div className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                            Risk Assessment
                          </div>
                          <div className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">
                            Investment Conclusion
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between">
                        <a 
                          href="#" 
                          className="bg-gray-100 text-gray-700 px-3 py-1 rounded-md text-sm flex items-center"
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Preview
                        </a>
                        <a 
                          href="#" 
                          className="bg-pale-gold text-midnight-navy px-3 py-1 rounded-md text-sm flex items-center"
                        >
                          <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download PDF
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-electric-blue mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="text-gray-700 font-medium">Secure, AI-powered analysis</span>
                  </div>
                  <span className="text-gray-500 text-sm">Step {activeStep + 1} of {steps.length}</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ResearchWorkflowDemo;
